// haystack: the index drawn as a field of dots behind the page. Dots land
// at random, flashing, as the index downloads. During a search one slow
// band of light crosses it, left to right, from the search's start to its
// results (PIR reads every record, which is why the servers learn nothing);
// on the way a scatter of dots warms into candidates and narrows to the
// results.
//
// Every dot eases toward a target brightness, so a change of phase is a slow
// fade rather than a jump; once nothing is moving the field stops drawing.

import { $, allDocs, count, h, link, MB, Result, splitURL, startEngine, STEPS } from "./engine";
import { describe } from "./describe";

// ── The field ──

const canvas = $<HTMLCanvasElement>("field");
const ctx = canvas.getContext("2d")!;
const GAP = 12; // css px between dots
const CANDIDATES = 0.012; // the share of dots the decryption brings up
let W = 0, H = 0, N = 0, cols = 0;
let xs = new Float32Array(0), ys = new Float32Array(0);
// rank[i]: dot i's place in a fixed random order; the candidates are the
// dots ranked first, and the results the first few of those.
let rank = new Uint32Array(0);
let ink = new Float32Array(0), straw = new Float32Array(0); // current alphas
// star[i]: how bright dot i shines as a star once the page has loaded, in
// (0, 1]; 0 for the dots that aren't stars.
let star = new Float32Array(0);

function layout() {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  W = window.innerWidth;
  H = window.innerHeight;
  canvas.width = Math.round(W * dpr);
  canvas.height = Math.round(H * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  cols = Math.floor(W / GAP);
  const rows = Math.floor(H / GAP);
  const ox = (W - (cols - 1) * GAP) / 2, oy = (H - (rows - 1) * GAP) / 2;
  N = cols * rows;
  xs = new Float32Array(N); ys = new Float32Array(N);
  ink = new Float32Array(N); straw = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    xs[i] = ox + (i % cols) * GAP;
    ys[i] = oy + Math.floor(i / cols) * GAP;
  }
  // A seeded shuffle: the same candidates every time, and after a resize.
  const order = Array.from({ length: N }, (_, i) => i);
  let seed = 7;
  const rand = () => ((seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
  for (let i = N - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  rank = new Uint32Array(N);
  order.forEach((dot, r) => (rank[dot] = r));
  // A few dots are stars, mostly dim and a few bright, each nudged off the
  // grid so the sky doesn't read as a lattice.
  star = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    if (hash(rank[i] + 7) >= STAR_SHARE) continue;
    star[i] = 0.08 + 0.92 * Math.pow(hash(rank[i] + 11), 2.6);
    xs[i] += (hash(rank[i] + 13) - 0.5) * GAP * 0.8;
    ys[i] += (hash(rank[i] + 17) - 0.5) * GAP * 0.8;
  }
  // Start from where the field should be, not from black.
  for (let i = 0; i < N; i++) [ink[i], straw[i]] = target(i, 0);
}

type Phase = "loading" | "idle" | "local" | "sealed" | "answer" | "decrypt" | "fetch" | "done";
let phase: Phase = "loading";
let loadFrac = 0;
let results = 10; // how many dots the results are

const BASE = 0.24; // a landed dot while loading
const STAR_SHARE = 0.05; // the share of dots that are stars
const STAR_MIN = 0.06, STAR_MAX = 0.42; // the dimmest and brightest star's alpha
const GRID = 0.03; // the other dots' alpha: a very faint grid behind the stars
// A dot's brightness at rest once loaded: a night sky. The stars show, each
// steady at its own brightness, over a very faint grid.
const rest = (i: number) => (star[i] ? STAR_MIN + (STAR_MAX - STAR_MIN) * star[i] : GRID);
const DOT = 2.6; // css px
const GLOW = 0.55; // how strongly a gold dot's halo shows
const HALO = 14; // the halo's size, css px
// A halo, drawn once: a radial fade of one colour ("r,g,b").
function makeHalo(rgb: string) {
  const c = document.createElement("canvas");
  const px = 64;
  c.width = c.height = px;
  const g = c.getContext("2d")!;
  const grad = g.createRadialGradient(px / 2, px / 2, 0, px / 2, px / 2, px / 2);
  grad.addColorStop(0, `rgba(${rgb},1)`);
  grad.addColorStop(0.35, `rgba(${rgb},0.35)`);
  grad.addColorStop(1, `rgba(${rgb},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, px, px);
  return c;
}
const halo = makeHalo("255,200,70"); // round a gold dot
const starHalo = makeHalo("225,230,255"); // round a bright star, a little cool
const STAR_GLOW = 0.3; // how strongly a star's halo shows
const SCAN_W = 70; // its half-width, css px
const SCAN_GOLD = 1; // the band's gold at its middle
const SCAN_READ = 0.42; // the gold it leaves behind
const SCAN_GOLD_SHARE = 0.6; // the share of dots the band turns gold; the rest flare gray

// The band's pass takes as long as a search is expected to: the last one
// in this browser, or SEARCH_MS before any.
const SEARCH_MS = 12000;
const SEARCH_KEY = "haystack.searchMs";
const MIN_PASS_MS = 1500; // a pass no quicker than this, however quick the search
const EXIT_MS = 900; // the band's run off the right once the results are in, from the left edge
let searchT0 = 0, expectMs = SEARCH_MS;
let doneAt = 0, doneFrom = 0; // when the results came, and where the band was then
try {
  const v = Number(localStorage.getItem(SEARCH_KEY));
  if (v > 0) expectMs = v;
} catch {}

function startPass(now: number) {
  searchT0 = now;
  doneAt = 0;
}
function endPass(now: number, learn: boolean) {
  doneFrom = passAt(now);
  doneAt = now;
  if (!learn) return;
  expectMs = now - searchT0;
  try { localStorage.setItem(SEARCH_KEY, String(Math.round(expectMs))); } catch {}
}
// How far across the band is, 0 at the left edge and 1 past the right: in
// step with the expected time, slowing near the end so a slow search never
// runs it out; once the results are in, straight off the right.
function passAt(now: number): number {
  if (doneAt) {
    const u = Math.min(1, (now - doneAt) / (EXIT_MS * Math.max(0.3, 1 - doneFrom)));
    return doneFrom + (1 - doneFrom) * (1 - (1 - u) * (1 - u));
  }
  const x = (now - searchT0) / Math.max(expectMs, MIN_PASS_MS);
  return x < 0.85 ? x : 0.85 + 0.12 * (1 - Math.exp(-(x - 0.85) / 0.4));
}

// A fixed value in [0, 1) per integer: a dot's own randomness, the same
// every frame.
const hash = (n: number) => (Math.imul(n ^ 0x9e3779b9, 0x85ebca6b) >>> 8) / 0x1000000;

const phaseOf = (step: string): Phase =>
  step === "embed" || step === "centroids" ? "local"
  : step === "keywords" || step === "query" ? "sealed"
  : step === "answer" ? "answer"
  : step === "reconstruct" || step === "rank" ? "decrypt"
  : "fetch";

// What dot i should look like now: [ink alpha, straw alpha].
function target(i: number, now: number): [number, number] {
  const cand = rank[i] < N * CANDIDATES, result = rank[i] < results;
  switch (phase) {
    case "loading": {
      // Dots land in the random order as the download goes, each flashing
      // straw as it arrives, over a faint twinkle.
      const r = rank[i] / N;
      if (r > loadFrac) return [0.06, 0];
      if (loadFrac < 1 && loadFrac - r < 0.015) return [0, 1];
      return [BASE + 0.08 * Math.sin(now / 1430 + rank[i] * 2.399), 0];
    }
    case "idle":
      return [rest(i), 0];
    default: {
      // A search: one slow band of gold across all of it, left to right;
      // what it has passed keeps a faint gold, read. Within the band dots
      // differ: most turn gold, each its own shade, the rest flare gray,
      // and each meets the band a little early or late, so its edge is
      // ragged. Only the gold ones keep a trace. The candidates and then the
      // results light up wherever they are; once the band is gone the trace
      // fades and the results stay.
      const p = passAt(now);
      if (phase === "done" && p >= 1) return result ? [0, 1] : [rest(i), 0];
      const bx = -SCAN_W * 2 + p * (W + SCAN_W * 4);
      const v = hash(rank[i]), jitter = (hash(rank[i] + 1) - 0.5) * SCAN_W * 1.2;
      const x = xs[i] + jitter;
      const d = (x - bx) / SCAN_W;
      const band = Math.exp(-d * d);
      const lit = phase === "decrypt" ? (cand ? 0.85 : 0)
        : phase === "fetch" ? (result ? 1 : cand ? 0.35 : 0)
        : phase === "done" ? (result ? 1 : 0) : 0;
      if (lit) return [0, Math.max(lit, SCAN_GOLD * band)];
      if (v >= SCAN_GOLD_SHARE) return [rest(i) + (0.75 - rest(i)) * band, 0];
      const shade = 0.55 + 0.45 * (v / SCAN_GOLD_SHARE);
      const read = x < bx ? SCAN_READ * shade : 0;
      return [rest(i) * (1 - band), Math.max(read, SCAN_GOLD * shade * band)];
    }
  }
}

const scanning = () => phase !== "loading" && phase !== "idle" && passAt(performance.now()) < 1;

function draw(dt: number, now: number) {
  // Ease each dot toward its target: about a third of a second to settle.
  // Loading draws its flicker as is: eased, the flashes would blur away.
  const k = phase === "loading" ? 1 : 1 - Math.exp(-dt / 320);
  const inkB: number[][] = [], strawB: number[][] = [];
  let moving = scanning() || phase === "loading";
  const LV = 64; // alpha steps: fine enough for dim stars, and smooth fades
  for (let l = 0; l < LV; l++) { inkB.push([]); strawB.push([]); }
  for (let i = 0; i < N; i++) {
    const [ti, ts] = target(i, now);
    const di = ti - ink[i], ds = ts - straw[i];
    if (Math.abs(di) > 0.002 || Math.abs(ds) > 0.002) moving = true;
    ink[i] += di * k;
    straw[i] += ds * k;
    const li = Math.round(ink[i] * (LV - 1)), ls = Math.round(straw[i] * (LV - 1));
    if (li > 0) inkB[li].push(i);
    if (ls > 0) strawB[ls].push(i);
  }
  ctx.clearRect(0, 0, W, H);
  // Glow first, under the dots: a soft gold halo round each gold dot, added
  // together so a dense patch -- the scan, the results -- lights up.
  ctx.globalCompositeOperation = "lighter";
  for (let l = Math.ceil(LV * 0.13); l < LV; l++) {
    if (!strawB[l].length) continue;
    ctx.globalAlpha = (l / (LV - 1)) * GLOW;
    for (const i of strawB[l]) ctx.drawImage(halo, xs[i] - HALO / 2, ys[i] - HALO / 2, HALO, HALO);
  }
  // And a faint glow round the bright stars.
  for (let l = Math.ceil(LV * 0.3); l < LV; l++) {
    ctx.globalAlpha = (l / (LV - 1)) * STAR_GLOW;
    for (const i of inkB[l]) {
      if (!star[i]) continue;
      const s = HALO * (0.6 + star[i]);
      ctx.drawImage(starHalo, xs[i] - s / 2, ys[i] - s / 2, s, s);
    }
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "source-over";
  for (let l = 1; l < LV; l++) {
    const a = (l / (LV - 1)).toFixed(3);
    if (inkB[l].length) {
      ctx.fillStyle = `rgba(235,232,225,${a})`;
      ctx.beginPath();
      // A star is round, and the brighter it shines the larger.
      for (const i of inkB[l]) {
        if (!star[i]) { ctx.rect(xs[i] - DOT / 2, ys[i] - DOT / 2, DOT, DOT); continue; }
        const rad = (DOT / 2) * (0.9 + 0.8 * star[i]);
        ctx.moveTo(xs[i] + rad, ys[i]);
        ctx.arc(xs[i], ys[i], rad, 0, 2 * Math.PI);
      }
      ctx.fill();
    }
    if (strawB[l].length) {
      ctx.fillStyle = `rgba(255,206,72,${a})`;
      ctx.beginPath();
      // The brighter a straw dot, the larger: the results stand out.
      for (const i of strawB[l]) {
        const s = (l > LV * 0.7 && (phase === "fetch" || phase === "done") ? 1.6 : 1) * DOT / 2;
        ctx.rect(xs[i] - s, ys[i] - s, 2 * s, 2 * s);
      }
      ctx.fill();
    }
  }
  return moving;
}

let raf = 0, last = 0;
function loop(now: number) {
  const dt = last ? Math.min(now - last, 100) : 16;
  last = now;
  if (draw(dt, now)) raf = requestAnimationFrame(loop);
  else { raf = 0; last = 0; }
}
function kick() { if (!raf && !document.hidden) raf = requestAnimationFrame(loop); }
function setPhase(p: Phase) {
  phase = p;
  kick();
}
document.addEventListener("visibilitychange", () => {
  if (document.hidden) { cancelAnimationFrame(raf); raf = 0; last = 0; } else kick();
});
window.addEventListener("resize", () => { layout(); kick(); });
layout();
kick();

// ── The page ──

const q = $<HTMLInputElement>("q");
// Enter searches once the client is ready, and not while a search runs.
let canSearch = false;
const out = $("out");

const engine = startEngine({
  onLoad(s) {
    loadFrac = s.frac;
    kick();
    $("bar").style.width = `${(s.frac * 100).toFixed(1)}%`;
    $("load-title").textContent = s.detail ? `${s.title} · ${s.detail}` : s.title;
    $("load-pct").textContent = `${Math.floor(s.frac * 100)}%`;
  },
  onReady(numDocs) {
    $("load").hidden = true;
    setPhase("idle");
    canSearch = true;
    if (numDocs) {
      $("count").textContent = `${count(numDocs)} pages`;
    }
    showStored();
    q.focus();
  },
  onLoadError(msg) {
    $("load-title").textContent = msg;
    $("load-pct").textContent = "";
    $("load-retry").hidden = false;
  },
  onNotKept() {
    $("load-note").hidden = false;
  },
});

// Trying a failed load again downloads only the files it did not finish.
$("load-retry").addEventListener("click", () => {
  $("load-retry").hidden = true;
  $("load-title").textContent = "Starting";
  engine.retry();
});

async function showStored() {
  const n = await engine.stored();
  $("foot").hidden = n <= 0;
  $("stored").textContent = `${MB(n)} MB`;
}
$("clear").addEventListener("click", async (e) => {
  e.preventDefault();
  if (!confirm("Remove the saved models and index from this browser?")) return;
  await engine.clearStored();
  showStored();
});

function home() {
  if (document.body.classList.contains("busy")) return;
  document.body.classList.remove("searched", "has-results");
  out.innerHTML = "";
  setPhase("idle");
  q.value = "";
  q.focus();
}
$("home").addEventListener("click", home);
$("home").addEventListener("keydown", (e) => { if (e.key === "Enter") home(); });

// runSearch searches query and shows what comes back. verbatim searches it
// exactly as typed: what "Search instead for" runs after a correction.
async function runSearch(query: string, verbatim = false) {
  if (!query || !canSearch) return;
  canSearch = false;
  q.blur();
  out.innerHTML = "";
  document.body.classList.add("searched", "busy");
  document.body.classList.remove("has-results");
  const t0 = performance.now();
  const timer = setInterval(() => ($("t").textContent = `${((performance.now() - t0) / 1000).toFixed(1)} s`), 100);
  results = 10;
  startPass(performance.now());
  setPhase("local");
  $("what").textContent = STEPS[0].text;
  try {
    const res = await engine.search(query, (id, i) => {
      setPhase(phaseOf(id));
      $("what").textContent = i >= 0 ? STEPS[i].text : id;
    }, verbatim);
    results = Math.min(10, allDocs(res).length);
    endPass(performance.now(), true);
    setPhase("done");
    render(res);
  } catch (err: any) {
    setPhase("idle");
    out.append(h("p", "empty", `That search didn't finish: ${err.message}`));
  } finally {
    clearInterval(timer);
    $("t").textContent = "";
    document.body.classList.remove("busy");
    canSearch = true;
  }
}

$("form").addEventListener("submit", (e) => {
  e.preventDefault();
  runSearch(q.value.trim());
});

// A link that runs a search: "Search instead for", "Did you mean". The box
// shows the query that link searches.
function searchLink(text: string, query: string, verbatim: boolean): HTMLAnchorElement {
  const a = h("a", "again");
  a.href = "#";
  a.append(text);
  a.addEventListener("click", (e) => {
    e.preventDefault();
    q.value = query;
    runSearch(query, verbatim);
  });
  return a;
}

// The corrected query with the corrected words in bold.
function corrected(sp: NonNullable<Result["Spelling"]>): HTMLElement {
  const b = h("span", "fixed");
  const changed = new Set(sp.Changes.map((c) => c.Index));
  sp.Corrected.split(/\s+/).forEach((w, i) => {
    if (i) b.append(" ");
    b.append(changed.has(i) ? h("b", "", w) : w);
  });
  return b;
}

// What the client made of the query, above the results: a helpline when the
// query reads as someone in crisis, the spelling it corrected or would, and
// the address the query itself is.
function notes(res: Result) {
  if (res.Crisis) {
    const c = h("div", "crisis");
    c.setAttribute("role", "note");
    c.append(h("strong", "", "You don't have to go through this alone."), " ");
    c.append("In the US, call or text ");
    const tel = h("a", "", "988");
    tel.href = "tel:988";
    c.append(tel, " (Suicide & Crisis Lifeline) any time. Elsewhere, ");
    const intl = h("a", "", "findahelpline.com");
    intl.href = "https://findahelpline.com/";
    intl.target = "_blank";
    intl.rel = "noopener noreferrer";
    c.append(intl, " lists free, confidential helplines.");
    out.append(c);
  }
  const sp = res.Spelling;
  if (sp && sp.Applied) {
    const p = h("p", "spell");
    p.append("Showing results for ", corrected(sp));
    const p2 = h("p", "spell instead");
    p2.append("Search instead for ", searchLink(res.Query, res.Query, true));
    out.append(p, p2);
  } else if (sp) {
    const p = h("p", "spell");
    const a = searchLink("", sp.Corrected, false);
    a.append(corrected(sp));
    p.append("Did you mean ", a, "?");
    out.append(p);
  }
  if (res.Unfiltered) {
    out.append(h("p", "spell", "Nothing this search found matches the site: or filetype: you asked for, so here is everything it found."));
  }
  if (res.Direct) {
    const { host, path } = splitURL(res.Direct);
    const go = link(res.Direct, "direct", ["Go to ", h("span", "host", host), h("span", "path", path === "/" ? "" : path)]);
    out.append(go);
  }
}

const PAGE = 10;

function render(res: Result) {
  document.body.classList.add("has-results");
  notes(res);
  const docs = allDocs(res);
  const secs = (res.Timing.Total / 1e9).toFixed(1);
  const meta = h("p", "meta");
  const sep = () => h("span", "sep", "/");
  meta.append(`${docs.length} results`, sep(), `${secs} s`, sep(), `${MB(res.Comm.Upload + res.Comm.Download)} MB exchanged`);
  out.append(meta);
  if (!docs.length) {
    out.append(h("p", "empty", "Nothing turned up. Try fewer or different words."));
    return;
  }
  const list = h("ol", "results");
  out.append(list);
  let shown = 0;
  const more = h("button", "more");
  const showMore = () => {
    const from = shown;
    shown = Math.min(shown + PAGE, docs.length);
    for (let i = from; i < shown; i++) {
      const d = docs[i];
      const li = h("li");
      li.style.animationDelay = `${(i - from) * 45}ms`;
      li.append(h("span", "rank", String(i + 1).padStart(2, "0")));
      if (d.URL) {
        const { host, path, href } = splitURL(d.URL);
        const a = link(d.URL, "res", [h("span", "host", host), h("span", "path", path)]);
        a.title = href;
        const body = h("div", "body");
        body.append(a);
        // Read off the URL alone: nothing is fetched for it.
        const about = describe(d.URL);
        if (about) {
          const line = h("p", "desc");
          const bits: [string, string | undefined][] = [["site", about.site], ["kind", about.kind], ["title", about.title], ["date", about.date]];
          for (const [cls, text] of bits) {
            if (!text) continue;
            if (line.childNodes.length) line.append(h("span", "sep", "·"));
            line.append(h("span", cls, text));
          }
          body.append(line);
        }
        li.append(body);
      } else {
        li.append(h("span", "nourl", `document ${d.DocID} · address not retrieved`));
      }
      list.append(li);
    }
    more.textContent = `${Math.min(PAGE, docs.length - shown)} more, already here`;
    more.hidden = shown >= docs.length;
  };
  more.addEventListener("click", showMore);
  out.append(more);
  showMore();
}

q.focus();
